import { useEffect, useState } from "react";
import { toast } from "sonner";
import { startAuthentication, startRegistration, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { KeyRound, Smartphone, Copy, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg, setReauthHandler } from "@/lib/api";

export const rpId = () => window.location.hostname;
const inp = "h-11 w-full rounded-lg border border-[#E6E1D7] bg-white px-3 text-sm outline-none focus:border-stone-900";

export async function passkeyAssertion(optionsPath) {
  const { data } = await api.post(optionsPath, { rp_id: rpId() });
  const credential = await startAuthentication({ optionsJSON: data.options });
  return { challenge_id: data.challenge_id, credential };
}

export function FactorForm({ methods = ["totp", "recovery"], submit, passkeyOptionsPath, label = "Verify", withPassword }) {
  const [mode, setMode] = useState(methods.includes("totp") ? "totp" : methods.includes("passkey") ? "passkey" : "recovery");
  const [v, setV] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (payload) => { setBusy(true); try { await submit(payload); } catch (e) { toast.error(e?.name === "NotAllowedError" ? "Passkey was cancelled" : errMsg(e)); } finally { setBusy(false); } };
  if (withPassword) return (
    <form onSubmit={(e) => { e.preventDefault(); go({ password: pw }); }} className="space-y-3">
      <input type="password" autoComplete="current-password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} className={inp} data-testid="reauth-password" />
      <Button className="w-full" disabled={busy || !pw} data-testid="reauth-submit">{label}</Button>
    </form>);
  return (
    <div className="space-y-3" data-testid="mfa-factor-form">
      {methods.includes("passkey") && browserSupportsWebAuthn() && (
        <Button type="button" variant={mode === "passkey" ? "default" : "outline"} className="w-full" disabled={busy}
          onClick={async () => { setMode("passkey"); setBusy(true); try { await submit(await passkeyAssertion(passkeyOptionsPath)); } catch (e) { toast.error(e?.name === "NotAllowedError" ? "Passkey was cancelled" : errMsg(e)); } finally { setBusy(false); } }} data-testid="mfa-use-passkey">
          <KeyRound className="h-4 w-4 mr-2" />Use passkey or security key</Button>)}
      {mode !== "passkey" && <form onSubmit={(e) => { e.preventDefault(); go(mode === "totp" ? { code: v } : { recovery_code: v }); }} className="space-y-3">
        <input autoFocus inputMode={mode === "totp" ? "numeric" : "text"} maxLength={mode === "totp" ? 6 : 20} placeholder={mode === "totp" ? "6-digit code" : "Recovery code"} value={v}
          onChange={(e) => setV(mode === "totp" ? e.target.value.replace(/\D/g, "") : e.target.value.toUpperCase())} className={`${inp} text-center font-mono tracking-[0.3em]`} data-testid="mfa-code-input" />
        <Button className="w-full" disabled={busy || (mode === "totp" ? v.length !== 6 : v.length < 8)} data-testid="mfa-code-submit">{busy ? "Verifying…" : label}</Button>
      </form>}
      <div className="flex justify-center gap-4 text-xs text-stone-500">
        {methods.includes("totp") && mode !== "totp" && <button type="button" onClick={() => { setMode("totp"); setV(""); }} data-testid="mfa-mode-totp">Use authenticator code</button>}
        {mode !== "recovery" && <button type="button" onClick={() => { setMode("recovery"); setV(""); }} data-testid="mfa-mode-recovery">Use a recovery code</button>}
      </div>
    </div>
  );
}

export function RecoveryCodes({ codes }) {
  const text = codes.join("\n");
  return (
    <div className="space-y-3" data-testid="recovery-codes">
      <p className="text-sm text-stone-600">Save these one-time recovery codes somewhere safe. Each works once if you lose your phone or passkey. They won't be shown again.</p>
      <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-[#F3EFE6] rounded-lg p-4">{codes.map((c) => <span key={c}>{c}</span>)}</div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" className="flex-1" onClick={() => { navigator.clipboard?.writeText(text); toast.success("Copied"); }}><Copy className="h-4 w-4 mr-1" />Copy</Button>
        <Button type="button" variant="outline" className="flex-1" onClick={() => { const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([text])); a.download = "86d-recovery-codes.txt"; a.click(); }}><Download className="h-4 w-4 mr-1" />Download</Button>
      </div>
    </div>
  );
}

export function MfaEnroll({ onDone }) {
  const [qr, setQr] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const finish = (data) => { if (data.recovery_codes) setCodes(data.recovery_codes); else onDone?.(); };
  const totp = async () => { try { setQr((await api.post("/security/mfa/totp/start")).data); } catch (e) { toast.error(errMsg(e)); } };
  const verify = async (e) => { e.preventDefault(); setBusy(true); try { finish((await api.post("/security/mfa/totp/verify", { code })).data); setQr(null); setCode(""); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); } };
  const passkey = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/security/mfa/passkey/options", { rp_id: rpId() });
      const credential = await startRegistration({ optionsJSON: data.options });
      finish((await api.post("/security/mfa/passkey/register", { challenge_id: data.challenge_id, credential, name: navigator.platform || "Passkey" })).data);
      toast.success("Passkey added");
    } catch (e) { toast.error(e?.name === "NotAllowedError" ? "Passkey was cancelled" : errMsg(e)); } finally { setBusy(false); }
  };
  if (codes) return <div className="space-y-3"><RecoveryCodes codes={codes} /><Button className="w-full" onClick={() => { setCodes(null); onDone?.(); }} data-testid="recovery-codes-done">I saved my codes</Button></div>;
  if (qr) return (
    <form onSubmit={verify} className="space-y-3" data-testid="totp-enroll">
      <p className="text-sm text-stone-600">Scan with Google Authenticator, 1Password, Authy or Microsoft Authenticator, then enter the code.</p>
      <img src={qr.qr} alt="Authenticator QR code" className="h-44 w-44 mx-auto rounded-lg border border-[#E6E1D7]" data-testid="totp-qr" />
      <code className="block text-center text-xs font-mono break-all" data-testid="totp-secret">{qr.secret}</code>
      <input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" className={`${inp} text-center font-mono tracking-[0.4em]`} data-testid="totp-code" />
      <Button className="w-full" disabled={busy || code.length !== 6} data-testid="totp-verify">Turn on</Button>
    </form>);
  return (
    <div className="grid gap-2" data-testid="mfa-enroll-options">
      {browserSupportsWebAuthn() && <Button variant="outline" className="justify-start h-12" disabled={busy} onClick={passkey} data-testid="mfa-add-passkey"><KeyRound className="h-4 w-4 mr-2" />Add a passkey or security key (most secure)</Button>}
      <Button variant="outline" className="justify-start h-12" onClick={totp} data-testid="mfa-add-totp"><Smartphone className="h-4 w-4 mr-2" />Use an authenticator app</Button>
      <p className="text-[11px] text-stone-500">SMS codes aren't offered — they can be intercepted.</p>
    </div>
  );
}

export function ReauthDialog() {
  const [resolver, setResolver] = useState(null);
  const [status, setStatus] = useState(null);
  useEffect(() => {
    setReauthHandler(async () => { const s = (await api.get("/security/mfa")).data; setStatus(s); return new Promise((res) => setResolver(() => res)); });
    return () => setReauthHandler(null);
  }, []);
  const close = (v) => { resolver?.(v); setResolver(null); };
  const methods = status ? [...(status.passkeys.length ? ["passkey"] : []), ...(status.totp ? ["totp"] : []), "recovery"] : [];
  return (
    <Dialog open={!!resolver} onOpenChange={(o) => !o && close(false)}>
      <DialogContent data-testid="reauth-dialog">
        <DialogHeader><DialogTitle>Confirm it's you</DialogTitle><DialogDescription>This is a sensitive action. Verify again to continue (valid for 10 minutes).</DialogDescription></DialogHeader>
        {status && <FactorForm methods={methods} withPassword={!status.enabled} passkeyOptionsPath="/security/reauth/passkey-options" label="Confirm"
          submit={async (p) => { await api.post("/security/reauth", p); toast.success("Verified"); close(true); }} />}
        <DialogFooter><Button variant="outline" onClick={() => close(false)}>Cancel</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MfaGate() {
  const done = () => window.location.reload();
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FBF9F5]" data-testid="mfa-gate">
      <div className="w-full max-w-md card-surface p-6 md:p-8">
        <div className="font-display text-3xl mb-1">Protect your account</div>
        <p className="text-sm text-stone-600 mb-6">Owners and anyone with financial or admin access must use two-step verification on every subscription plan. Add a passkey or an authenticator app to continue.</p>
        <MfaEnroll onDone={done} />
      </div>
    </div>
  );
}
